// What the Connections page and the run history say of an integration's runs (design system 06-modules §6, binding from
// v8.3.0; conformity findings D5, D6, S5 of 2026-10-05, fixed as group A by the owner's picks). Pure; unit-tested under
// node. The server words a run's reason; this module words its state, its trigger, the unattended streak and the
// retention window — every word from 05-content §6 or spelled here once, never a stored code.

import type { ConnectionRunDto, ConnectionRunsResponse, ConnectionRunsSummary } from '../../../shared/api-types.js';
import {
  CONNECTION_RUN_STATE_LABELS,
  CONNECTION_RUN_TRIGGER_LABELS,
  CONNECTION_SYSTEM_LABELS,
  type ConnectionRunState,
  type ConnectionRunTrigger,
  type ConnectionSystem,
} from '../../../shared/enums.js';
import { formatCount, pluralise } from '../../ui/format.js';
import type { IconName } from '../../ui/Icon.js';
import type { DirectionWords } from '../../ui/sort-params.js';
import type { Tone } from '../../ui/StatusPill.js';

/** Where a system's run history lives: reached from the system's section, arriving with the system identified (§6.3 R2). */
export function connectionRunsPath(system: ConnectionSystem): string {
  return `/connections/${system}/runs`;
}

/** What one run of the system is called to the reader: Fellow syncs, Slack refreshes the channel list. */
export function runNoun(system: ConnectionSystem): string {
  return system === 'fellow' ? 'sync' : 'refresh';
}

/** A run as the reader counts it: each form a complete word, chosen by count (05-content §8) — never a suffix on a stem. */
const RUN_FORMS: Readonly<Record<ConnectionSystem, { one: string; other: string }>> = {
  fellow: { one: 'sync', other: 'syncs' },
  slack: { one: 'refresh', other: 'refreshes' },
};

/** The history page's title, naming the system and what its runs are (§6.3 R2 "arriving with that system identified"). */
export function runHistoryTitle(system: ConnectionSystem): string {
  return system === 'fellow' ? 'Fellow syncs' : 'Slack channel-list refreshes';
}

export interface RunStatePresentation {
  word: string;
  tone: Tone;
  icon: IconName;
  detail: string;
}

/** A run state's word with a glyph (05-content §6; 01-foundations §7.3: never colour alone). */
export function runStatePresentation(state: ConnectionRunState): RunStatePresentation {
  switch (state) {
    case 'running':
      return { word: CONNECTION_RUN_STATE_LABELS.running, tone: 'info', icon: 'refresh', detail: 'This run is under way now.' };
    case 'completed':
      return { word: CONNECTION_RUN_STATE_LABELS.completed, tone: 'success', icon: 'check', detail: 'This run finished well.' };
    case 'failed':
      return { word: CONNECTION_RUN_STATE_LABELS.failed, tone: 'danger', icon: 'alert-circle', detail: 'This run did not finish; its reason says why.' };
    case 'skipped':
      return { word: CONNECTION_RUN_STATE_LABELS.skipped, tone: 'neutral', icon: 'minus', detail: 'Nothing was done: a run was already queued or under way.' };
    case 'refused':
      return { word: CONNECTION_RUN_STATE_LABELS.refused, tone: 'warning', icon: 'lock', detail: 'Your access did not allow this run; nothing broke.' };
  }
}

/** The State pill's word with the run it is the word of — null for Pending, which names a run that has not begun. */
export interface LastRunPresentation extends RunStatePresentation {
  run: ConnectionRunDto | null;
}

/**
 * The State pill of the most recent run (§6.4 State: "of the most recent run … Running, Completed, Failed, Skipped"):
 * the run's own word where one is recorded as running, or `Pending` where a run is queued and no record has begun —
 * it is waiting to start, which 05-content §6 words so and never as Running. Null where there has been no run: the row
 * then says "— never" (§6.7), since a system that never ran has no state to pill. `run` is the record the word
 * describes, so the row's time names that run and no other: Pending has none (review of 2026-10-05, finding 13 — the
 * row had read "Pending of the sync 12 minutes ago", the word for the queued run and the time for the previous one).
 */
export function lastRunStatePresentation(summary: Pick<ConnectionRunsSummary, 'last'>, syncInProgress: boolean): LastRunPresentation | null {
  const last = summary.last;
  if (last !== null && last.state === 'running') return { ...runStatePresentation('running'), run: last };
  if (syncInProgress) return { word: 'Pending', tone: 'info', icon: 'clock', detail: 'A run is queued and waits for a worker.', run: null };
  return last === null ? null : { ...runStatePresentation(last.state), run: last };
}

export interface RunTriggerPresentation {
  word: string;
  icon: IconName;
}

/**
 * How a run began, worded as that and never as a state (§6.4 Trigger). A run started by hand is told apart from the
 * schedule's by its word and its glyph alike (§6.3 R2: "visible and distinguished").
 */
export function runTriggerPresentation(trigger: ConnectionRunTrigger): RunTriggerPresentation {
  switch (trigger) {
    case 'schedule':
      return { word: CONNECTION_RUN_TRIGGER_LABELS.schedule, icon: 'clock' };
    case 'hand':
      return { word: CONNECTION_RUN_TRIGGER_LABELS.hand, icon: 'user' };
    case 'cli':
      return { word: CONNECTION_RUN_TRIGGER_LABELS.cli, icon: 'bolt' };
  }
}

/** The "— never" reading where there has been no run, no success or no trigger to speak of (§6.4 Last success; §6.7). */
export const NEVER = '— never';

/**
 * The Unattended streak row (§6.4): how many scheduled runs in a row completed by themselves, and which trigger it
 * counts — "a streak that a person can top up measures that person's attention, not the system's reliability".
 */
export function unattendedStreakText(summary: Pick<ConnectionRunsSummary, 'unattendedStreak'>, system: ConnectionSystem): { word: string; detail: string } {
  const noun = runNoun(system);
  const forms = RUN_FORMS[system];
  const detail = `Counts only ${forms.other} the schedule started; one started by hand or from the command line never adds to it.`;
  if (summary.unattendedStreak <= 0) return { word: 'None yet', detail: `No scheduled ${noun} has completed since the last one failed, was refused, or the schedule last ran. ${detail}` };
  const word = pluralise(summary.unattendedStreak, { one: `# scheduled ${forms.one} in a row`, other: `# scheduled ${forms.other} in a row` });
  return { word, detail: `Each completed by itself. ${detail}` };
}

/**
 * The retention window, stated on the history itself (§6.3 R2: "a history silently cut at thirty days is read as a
 * complete one"), and the cap where the answer is full — in one sentence either way, since it is the page's subtitle
 * (02-patterns §2.1: "it MUST NOT be two sentences"; review of 2026-10-05, finding 12).
 */
export function retentionText(response: Pick<ConnectionRunsResponse, 'retentionDays' | 'limit' | 'runs' | 'system'>): string {
  // Why two whole templates (05-content §8; the record's "run history's tail", Phase 1c D30): the capped case was the
  // other's sentence with a clause spliced onto it.
  const runs = capitalise(RUN_FORMS[response.system].other);
  const days = formatCount(response.retentionDays);
  if (response.runs.length >= response.limit) return `${runs} of the last ${days} days are kept; this list shows the most recent ${formatCount(response.limit)} of them.`;
  return `${runs} of the last ${days} days are kept.`;
}

function capitalise(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/** The history's caption: the system's recent runs, newest first — the one place the order is the data (04-data §1.2). */
export function runHistoryCaption(system: ConnectionSystem): string {
  return `${CONNECTION_SYSTEM_LABELS[system]} ${RUN_FORMS[system].other}, newest first`;
}

/**
 * What the history's table holds, as its error and denied sentences name it ("We couldn't load your Fellow syncs") — the
 * product's name keeping its capital (05-content §1; Phase 1c R19: the caption lowercased read "fellow syncs, newest first").
 */
export const RUN_HISTORY_NOUNS: Readonly<Record<ConnectionSystem, string>> = { fellow: 'your Fellow syncs', slack: 'your Slack refreshes' };

/** The history's footer, how many runs it lists: one complete string chosen by count (05-content §8). */
export function runsListedText(system: ConnectionSystem, count: number): string {
  const forms = RUN_FORMS[system];
  return pluralise(count, { one: `# ${forms.one} listed.`, other: `# ${forms.other} listed.` });
}

/**
 * The State column's own direction words (04-data §1.2: the words name what the reader gets): completed first, or
 * failed first — never "first to last" for a word with an order of its own.
 */
export const RUN_STATE_DIRECTION_WORDS: DirectionWords = Object.freeze({ asc: 'completed first', desc: 'failed first' });
const RUN_STATE_RANK: Readonly<Record<ConnectionRunState, number>> = { completed: 0, running: 1, skipped: 2, refused: 3, failed: 4 };
export function runStateRank(state: ConnectionRunState): number {
  return RUN_STATE_RANK[state];
}

/** The Trigger column's direction words: the schedule's runs first, or those a person started first. */
export const RUN_TRIGGER_DIRECTION_WORDS: DirectionWords = Object.freeze({ asc: 'by the schedule first', desc: 'by hand first' });

/** The Reason column's direction words: it sorts by the reason's code, so the words say so; runs with none come last either way. */
export const RUN_REASON_DIRECTION_WORDS: DirectionWords = Object.freeze({ asc: 'by reason code, A to Z', desc: 'by reason code, Z to A' });
const RUN_TRIGGER_RANK: Readonly<Record<ConnectionRunTrigger, number>> = { schedule: 0, cli: 1, hand: 2 };
export function runTriggerRank(trigger: ConnectionRunTrigger): number {
  return RUN_TRIGGER_RANK[trigger];
}

/** How long a run took, in whole seconds, for the history's Took column; null while it runs. */
export function runDurationSeconds(run: Pick<ConnectionRunDto, 'startedAt' | 'endedAt'>): number | null {
  if (run.endedAt === null) return null;
  const ms = Date.parse(run.endedAt) - Date.parse(run.startedAt);
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.round(ms / 1000));
}

/** A duration in seconds as the reader says it: "under a second", "4 s", "2 min 5 s". */
export function durationText(seconds: number): string {
  if (seconds < 1) return 'under a second';
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`;
}

/** What a run moved, in the reader's units (§6.4 Coverage: "how much it moved, as a count in the reader's units"). */
export function movedText(run: Pick<ConnectionRunDto, 'system' | 'moved'>): string | null {
  if (run.moved === null) return null;
  return run.system === 'fellow'
    ? pluralise(run.moved, { one: '# meeting', other: '# meetings' })
    : pluralise(run.moved, { one: '# conversation', other: '# conversations' });
}
